import React, { useMemo, useState } from "react";
import {
  Paper,
  List,
  MenuItem,
  Menu,
  Popover,
  Typography,
  Avatar,
  Divider,
  Box,
  Chip,
  LinearProgress,
} from "@mui/material";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import BrightnessMediumOutlinedIcon from "@mui/icons-material/BrightnessMediumOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import ColorLensOutlinedIcon from "@mui/icons-material/ColorLensOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useNavigate } from "react-router-dom";
import { authService } from "../../services/authService"; 
import { useTokenUsage } from '../../hooks/useTokenUsage';


interface AccountMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  themeMode: string;
  setThemeMode: (newMode: "light" | "dark" | "auto") => void;
  onLogout?: () => void;
}

const AccountMenu: React.FC<AccountMenuProps> = ({
  anchorEl,
  open,
  onClose,
  themeMode,
  setThemeMode,
  onLogout,
}) => {
  const navigate = useNavigate();
  const authData = authService.getAuthData();
  const userInfo = authData?.user_info;

  const [themeAnchorEl, setThemeAnchorEl] = useState<HTMLElement | null>(null);
  const { tokenUsage, isLoading, formatTokenCount } = useTokenUsage(userInfo?.email);

  // Generate avatar initials from user's name
  const avatarInitials = useMemo(() => {
    if (!userInfo?.name) return "?";
    return userInfo.name
      .split(" ")
      .map((part: string) => part[0]) // Added type annotation
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }, [userInfo?.name]);

  const handleSettings = () => {
    navigate("/settings");
  };

  const handleLogout = () => {
    authService.clearAuthData();
    if (onLogout) {
      onLogout();
    } else {
      navigate("/login", { replace: true });
    }
  };

  // Theme Menu Handlers
  const openThemeMenu = (event: React.MouseEvent<HTMLElement>) => {
    setThemeAnchorEl(event.currentTarget);
  };

  const closeThemeMenu = () => {
    setThemeAnchorEl(null);
  };

  const handleThemeChange = (mode: "light" | "dark" | "auto") => {
    setThemeMode(mode);
    closeThemeMenu();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Paper sx={{ minWidth: 280 }}>
        {/* Avatar and User Info */}
        <div style={{ padding: 16, display: "flex", alignItems: "center" }}>
          <Avatar
            alt={userInfo?.name || "User"}
            variant="rounded"
            sx={{
              fontSize: 16,
              width: 32,
              height: 32,
              mr: 2,
              bgcolor: "primary.main",
            }}
          >
            {avatarInitials}
          </Avatar>
          <div>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {userInfo?.name || "Unknown User"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {userInfo?.email.toLowerCase() || "No email available"}
            </Typography>
          </div>
        </div>
        <Divider />

        {/* Token Usage Progress */}
        <Box sx={{ padding: 2 }}>
          <Typography variant="body2" color="textSecondary">
          {isLoading ? "กำลังโหลดข้อมูล..." : 
           tokenUsage ? `การใช้งานโทเค็น: ${formatTokenCount(tokenUsage.total_tokens.value)} โทเค็น` :
           "ไม่พบข้อมูลการใช้งาน"}          
           </Typography>
          <LinearProgress
            variant="determinate"
            color="primary"
            value={tokenUsage ? Math.min((tokenUsage.total_tokens.value / 1000000) * 100, 100) : 0}            
            sx={{ mt: 1, borderRadius: "16px" }}
          />
        </Box>
        <Divider />

        {/* Menu Items */}
        <List>
          {/* Theme Settings Dropdown */}
          <MenuItem onClick={openThemeMenu} sx={{ height: "32px" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <ColorLensOutlinedIcon
                  sx={{ mr: 1, fontSize: 16, color: "text.primary" }}
                />
                <Typography variant="body2" color="text.primary">
                  ตั้งค่าธีม
                </Typography>
              </Box>
              <Chip
                label={`${
                  themeMode === "light"
                    ? "โหมดสว่าง"
                    : themeMode === "dark"
                    ? "โหมดมืด"
                    : "ค่าเริ่มต้นระบบ"
                }`}
                size="small"
                sx={{
                  fontSize: "10px",
                  borderRadius: "4px",
                }}
              />
            </Box>
          </MenuItem>

          <Menu
            anchorEl={themeAnchorEl}
            open={Boolean(themeAnchorEl)}
            onClose={closeThemeMenu}
          >
            <MenuItem onClick={() => handleThemeChange("light")}>
              <CheckOutlinedIcon
                sx={{
                  mr: 1,
                  fontSize: 16,
                  color: "primary.main",
                  visibility: themeMode === "light" ? "visible" : "hidden",
                }}
              />
              <LightModeOutlinedIcon sx={{ mr: 1, fontSize: 16 }} />
              <Typography variant="body2">โหมดสว่าง</Typography>
            </MenuItem>
            <MenuItem onClick={() => handleThemeChange("dark")}>
              <CheckOutlinedIcon
                sx={{
                  mr: 1,
                  fontSize: 16,
                  color: "primary.main",
                  visibility: themeMode === "dark" ? "visible" : "hidden",
                }}
              />
              <DarkModeOutlinedIcon sx={{ mr: 1, fontSize: 16 }} />
              <Typography variant="body2">โหมดมืด</Typography>
            </MenuItem>
            <MenuItem onClick={() => handleThemeChange("auto")}>
              <CheckOutlinedIcon
                sx={{
                  mr: 1,
                  fontSize: 16,
                  color: "primary.main",
                  visibility: themeMode === "auto" ? "visible" : "hidden",
                }}
              />
              <BrightnessMediumOutlinedIcon sx={{ mr: 1, fontSize: 16 }} />
              <Typography variant="body2">ค่าเริ่มต้นระบบ</Typography>
            </MenuItem>
          </Menu>

          {/* Settings */}
          <MenuItem onClick={handleSettings} sx={{ height: "32px" }}>
            <SettingsOutlinedIcon
              sx={{ mr: 1, fontSize: 16, color: "text.primary" }}
            />
            <Typography variant="body2" color="text.primary">
              จัดการระบบ
            </Typography>
          </MenuItem>

          {/* Logout */}
          <MenuItem onClick={handleLogout} sx={{ height: "32px" }}>
            <LogoutOutlinedIcon
              sx={{ mr: 1, fontSize: 16, color: "secondary.main" }}
            />
            <Typography variant="body2" color="secondary.main">
              ออกจากระบบ
            </Typography>
          </MenuItem>
        </List>
      </Paper>
    </Popover>
  );
};

export default AccountMenu;
